import { Reload } from './route-optimization.interface';
import { RouteRecalc } from './route-recalc';
import { TruckRouteView } from './plan-result';
import {
  addMinutesToClock,
  outboundLegMin,
  returnLegMin,
  serviceMinBetween,
} from './route-times';

/**
 * Los tiempos de UNA ruta, en una forma que no dice de dónde salieron.
 *
 * Existe porque el gestor tiene que pintar el recorrido completo —salida del
 * almacén, horas por parada, regreso— con dos fuentes distintas según la pestaña:
 * el PLAN en la del optimizador y el RECÁLCULO en la personalizada. Si la
 * plantilla ramificara por origen, cada corrección habría que hacerla dos veces y
 * la que se olvide es la que se ve en producción.
 *
 * Por eso hay dos productores y **una sola forma**: `timelineFromPlan` y
 * `timelineFromRecalc` normalizan acá, y de ahí para adelante nadie pregunta.
 *
 * Módulo puro: entra una vista o un recálculo, sale el timeline. Sin HTTP, sin
 * Angular.
 */

/** Los tiempos de una parada dentro del recorrido. */
export interface StopTimeline {
  arrivalTime: string;
  departureTime: string;
  serviceMin: number;
  /** Viaje desde la parada anterior. `0` en la primera, por contrato del motor. */
  legDurationMin: number;
  /** Minutos desde el inicio del turno hasta llegar (incluye la atención acumulada). */
  etaMin: number;
  /** Viaje al que pertenece. `1` cuando el resultado no modela viajes. */
  trip: number;
  /**
   * `true` si el número es una ESTIMACIÓN y no un cálculo del motor.
   *
   * Vive en la parada y no solo en la ruta porque quien pinta un número es la fila:
   * la marca tiene que estar donde se lee, no en otra parte de la pantalla.
   */
  estimated: boolean;
}

/** El recorrido de una ruta: sus extremos y sus paradas. */
export interface RouteTimeline {
  /** Minutos del almacén a la primera entrega. */
  outboundMin: number;
  /** Hora a la que el camión sale del almacén. `''` si no se puede derivar. */
  departureTime: string;
  /** Minutos de la última parada de vuelta al almacén. */
  returnMin: number;
  /** Hora de llegada de vuelta. `''` si no se puede derivar. */
  returnArrivalTime: string;
  /**
   * Regresos al almacén entre viajes.
   *
   * Vacío cuando el resultado **no modela viajes** — un recálculo por `/sort`—, y
   * con eso el divisor de recarga desaparece solo, sin que la plantilla pregunte
   * por el origen. Pintarlo ahí afirmaría un regreso que ese cálculo no consideró.
   */
  reloads: Reload[];
  /**
   * Tiempos por `orderId`. Una parada **ausente** no tiene tiempos que afirmar —es
   * el caso de las que `Replanificar` no pudo ubicar— y eso se lee como ausencia,
   * no como ceros.
   */
  stops: Map<string, StopTimeline>;
  /**
   * `true` si TODO el recorrido es estimado.
   *
   * Nunca hay mezcla: el resolvedor devuelve tiempos reales o estimados para la ruta
   * entera. Eso es lo que hace legible la marca — dentro de una ruta no hay que
   * comparar número por número.
   */
  estimated: boolean;
}

/** Un timeline sin nada que afirmar. */
function emptyTimeline(): RouteTimeline {
  return {
    outboundMin: 0,
    departureTime: '',
    returnMin: 0,
    returnArrivalTime: '',
    reloads: [],
    stops: new Map(),
    estimated: false,
  };
}

/**
 * Los tiempos que el PLAN calculó para esta ruta.
 *
 * `TruckRouteView` ya derivó los cuatro números de ruta con las mismas funciones que
 * usa el otro productor, así que acá solo se proyecta: recalcularlos sería una
 * segunda aritmética que puede divergir de la que la lista del resultado muestra.
 */
export function timelineFromPlan(view: TruckRouteView | undefined): RouteTimeline {
  if (!view) return emptyTimeline();
  const stops = new Map<string, StopTimeline>();
  for (const s of view.stops ?? []) {
    stops.set(s.orderId, {
      arrivalTime: s.arrivalTime,
      departureTime: s.departureTime,
      serviceMin: s.serviceMin ?? 0,
      legDurationMin: s.legDurationMin ?? 0,
      etaMin: s.etaMin ?? 0,
      trip: s.trip ?? 1,
      estimated: false,
    });
  }
  return {
    estimated: false,
    outboundMin: view.outboundMin ?? 0,
    departureTime: view.departureTime ?? '',
    returnMin: view.returnMin ?? 0,
    returnArrivalTime: view.returnArrivalTime ?? '',
    reloads: view.reloads ?? [],
    stops,
  };
}

/**
 * Los tiempos que el motor calculó para la secuencia VIGENTE de una ruta editada.
 *
 * Los cuatro números de ruta **no vienen en la respuesta**: se derivan con las
 * MISMAS funciones que el plan (`outboundLegMin`, `returnLegMin`,
 * `addMinutesToClock`). Escribir una aritmética propia acá haría que la misma ruta
 * se leyera distinto según la pestaña.
 *
 * `returnLegMin` deriva el regreso por diferencia contra el viaje TOTAL, y eso vale
 * porque el motor informa viaje puro: `total_duration_min = route.duration / 60`, y
 * `route.duration` de VROOM excluye el servicio. Si incluyera la atención, el
 * regreso saldría negativo — y en silencio.
 *
 * `null` cuando no hay recálculo: la pestaña no pinta ni salida ni regreso, en vez
 * de pintar ceros.
 */
export function timelineFromRecalc(recalc: RouteRecalc | undefined): RouteTimeline | null {
  if (!recalc) return null;

  // En el orden de la secuencia, que es el que da sentido a "la primera parada".
  const ordered = recalc.sequence
    .map((orderId) => recalc.stops.get(orderId))
    .filter((t): t is NonNullable<typeof t> => !!t);

  const stops = new Map<string, StopTimeline>();
  for (const orderId of recalc.sequence) {
    const t = recalc.stops.get(orderId);
    if (!t) continue;
    stops.set(orderId, {
      arrivalTime: t.arrivalTime,
      departureTime: t.departureTime,
      // La atención se deriva de las dos horas de reloj y no se asume: es la misma
      // regla del plan, y con `/plan` es el servicio que el motor realmente asignó.
      serviceMin: serviceMinBetween(t.arrivalTime, t.departureTime),
      legDurationMin: t.legDurationMin,
      etaMin: t.etaMin,
      trip: t.trip ?? 1,
      estimated: false,
    });
  }

  const outboundMin = outboundLegMin(ordered);
  const returnMin = returnLegMin(ordered, recalc.totalDurationMin);
  const firstArrival = ordered[0]?.arrivalTime ?? '';

  return {
    estimated: false,
    outboundMin,
    departureTime: outboundMin > 0 ? addMinutesToClock(firstArrival, -outboundMin) : '',
    returnMin,
    returnArrivalTime: returnMin > 0 ? addMinutesToClock(recalc.endTime, returnMin) : '',
    reloads: recalc.reloads ?? [],
    stops,
  };
}
